import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '../components/common/Button';
import { observer } from 'mobx-react';
import Progress from '../components/common/Progress';
import { useStores } from '../stores';
import { TextField } from '@material-ui/core';
import { Checkbox, Box } from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import appStyles from '../../../config/appStyles.js';
import Text from '../components/common/Text';
import Icon from '../components/common/Icon';
import { isMobile } from '../utils/responsive';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { detectAlpha2Country } from '../../../utils/country.js';
import CrossLoginButton from '../components/CrossLoginButton.js';
import { countryCodes } from '../../../stores/Constants.js';

const useStyles = makeStyles((theme) => appStyles.login(theme));

const Login = observer((props) => {
  const { t } = useTranslation(['common', 'components']);
  const classes = useStyles();
  const { userStore } = useStores();
  const { errors, isLoading, isLogged } = userStore;
  const location = useLocation();
  const brand = process.env.REACT_APP_BRAND === 'PW' ? 'deagostini' : 'fanhome';
  const lang = detectAlpha2Country();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [checked, setChecked] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    if (window.snakeAnalytics) {
      window.snakeAnalytics.push([
        'event',
        'page_view',
        {
          context: {
            im_page_name: 'my-account/login',
            im_page_type: 'my-account/login',
            im_user_logged: isLogged ? 'yes' : 'no',
            im_language: lang,
            im_country: lang,
            im_brand: brand,
          },
        },
      ]);
    }
  }, []);

  return (
    <>
      <div
        id="login-form"
        className={classes.container}
        style={{ backgroundColor: '#fff' }}>
        {isLoading ? (
          <Progress />
        ) : (
          <>
            {process.env.REACT_APP_BRAND === 'FH' &&
              [countryCodes.de, countryCodes.uk, countryCodes.us].includes(
                detectAlpha2Country(),
              ) && <CrossLoginButton />}
            <form className={classes.root} noValidate autoComplete="off">
              <Typography className={classes.title} gutterBottom>
                {t('common:welcome')}
              </Typography>
              <Typography className={classes.secondTitle} gutterBottom>
                {t('common:signInEmail')}
              </Typography>
              <div style={{ marginBottom: !isMobile ? '24px' : 0 }}>
                <TextField
                  variant="filled"
                  label={t('common:user')}
                  value={username}
                  required
                  className={classes.input}
                  inputProps={{ 'aria-label': 'description' }}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <TextField
                  label={t('common:password')}
                  variant="filled"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  InputProps={{
                    endAdornment: (
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowPass(!showPass)}>
                        {showPass ? (
                          <Icon
                            className={classes.iconPass}
                            name="icon-password"></Icon>
                        ) : (
                          <Icon
                            className={classes.iconPass}
                            name="icon-password-disabled"></Icon>
                        )}
                      </div>
                    ),
                  }}
                  required
                  className={classes.input}
                  inputProps={{ 'aria-label': 'description' }}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className={classes.flex}>
                <div className={classes.keepmeinContainer}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                  />
                  <div className={classes.keepmein}>{t('common:keepMeIn')}</div>
                </div>
                <div className={classes.forgotPasswordContainer}>
                  <Link to={`/myarea/p/reset`} className={classes.forgotPass}>
                    {t('common:forgotPassword')}
                  </Link>
                </div>
              </div>
              <div>
                <Button
                  type="submit"
                  className={classes.button}
                  disabled={!(username && password)}
                  next
                  onClick={() =>
                    userStore.login(username, password, checked).catch(() => {
                      if (window.snakeAnalytics) {
                        window.snakeAnalytics.push([
                          'event',
                          'im_error',
                          {
                            eventData: {
                              im_message: 'incorrect_login',
                              im_location: location.pathname + location.search,
                            },
                          },
                        ]);
                      }
                    })
                  }>
                  {t('common:login')}
                </Button>
              </div>
              <div className={classes.errorText}>{errors}</div>
              <Box
                position="relative"
                mt={3}
                className={classes.activateYourAccountBox}>
                <hr></hr>

                <Text
                  variant="bodyXS"
                  className={classes.activateYourAccountText}>
                  <Link to={`/myarea/p/activateaccount/`}>
                    {t('components:ActivateAccount.loginTitle')}
                  </Link>
                </Text>
              </Box>
            </form>
          </>
        )}
      </div>
    </>
  );
});

export default Login;
